import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { ArrowLeft, Loader2, RotateCcw } from 'lucide-react';
import { useState } from 'react';
import {
  type ChatbotExecution,
  getChatbot,
  listExecutions,
  reprocessExecution,
} from './api';

const STATUS_LABELS: Record<ChatbotExecution['status'], string> = {
  running: 'Em execução',
  completed: 'Completado',
  transferred: 'Transferido',
  abandoned: 'Abandonado',
  errored: 'Erro',
};

const STATUS_COLORS: Record<ChatbotExecution['status'], string> = {
  running: 'bg-brand-50 text-brand-700',
  completed: 'bg-success-50 text-success-700',
  transferred: 'bg-violet-100 text-violet-700',
  abandoned: 'bg-n-100 text-n-700',
  errored: 'bg-danger-50 text-danger-700',
};

export function ChatbotExecutionsPage() {
  // biome-ignore lint/suspicious/noExplicitAny: dynamic param
  const params = useParams({ strict: false }) as any;
  const id = Number(params?.id);
  const qc = useQueryClient();

  const [statusFilter, setStatusFilter] = useState<ChatbotExecution['status'] | ''>('');
  const [selected, setSelected] = useState<ChatbotExecution | null>(null);

  const botQ = useQuery({
    queryKey: ['chatbot', id],
    queryFn: () => getChatbot(id),
    enabled: Number.isFinite(id),
  });

  const execQ = useQuery({
    queryKey: ['executions', id, statusFilter],
    queryFn: () =>
      listExecutions(id, statusFilter ? { status: statusFilter, limit: 100 } : { limit: 100 }),
    enabled: Number.isFinite(id),
  });

  const reprocessMut = useMutation({
    mutationFn: (execId: number) => reprocessExecution(execId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['executions', id] }),
  });

  return (
    <div className="flex h-full flex-1 flex-col gap-4 p-[24px_20px]">
      <header className="flex items-center gap-3">
        <Link
          // biome-ignore lint/suspicious/noExplicitAny: dynamic
          to={'/chatbot' as any}
          className="flex h-8 w-8 items-center justify-center rounded text-n-600 hover:bg-n-100"
        >
          <ArrowLeft size={16} />
        </Link>
        <div className="flex-1">
          <h1
            className="font-bold text-[20px] text-n-900"
            style={{ letterSpacing: '-.02em' }}
          >
            Execuções de {botQ.data?.name ?? '…'}
          </h1>
          <p className="text-[13px] text-n-500">
            Histórico de execuções, debug de fluxo e reprocessamento.
          </p>
        </div>
      </header>

      <div className="flex items-center gap-2">
        <span className="text-[12px] text-n-600">Status:</span>
        <select
          value={statusFilter}
          onChange={(e) =>
            setStatusFilter((e.target.value as ChatbotExecution['status'] | '') || '')
          }
          className="h-9 rounded border border-n-200 bg-n-0 px-2 text-[12px]"
        >
          <option value="">Todos</option>
          {(Object.keys(STATUS_LABELS) as ChatbotExecution['status'][]).map((s) => (
            <option key={s} value={s}>
              {STATUS_LABELS[s]}
            </option>
          ))}
        </select>
      </div>

      <div className="flex flex-1 gap-4 overflow-hidden">
        <div className="flex-1 overflow-auto rounded-md border border-n-200 bg-n-0 shadow-sm">
          <table className="w-full text-[12px]">
            <thead className="border-b border-n-200 bg-n-50 text-left">
              <tr>
                <th className="px-[14px] py-[10px] font-bold text-[11px] uppercase tracking-[.05em] text-n-500">
                  ID
                </th>
                <th className="px-[14px] py-[10px] font-bold text-[11px] uppercase tracking-[.05em] text-n-500">
                  Contato
                </th>
                <th className="px-[14px] py-[10px] font-bold text-[11px] uppercase tracking-[.05em] text-n-500">
                  Iniciada
                </th>
                <th className="px-[14px] py-[10px] font-bold text-[11px] uppercase tracking-[.05em] text-n-500">
                  Status
                </th>
                <th className="px-[14px] py-[10px] font-bold text-[11px] uppercase tracking-[.05em] text-n-500">
                  Nó atual
                </th>
                <th className="px-[14px] py-[10px] text-right font-bold text-[11px] uppercase tracking-[.05em] text-n-500">
                  Ação
                </th>
              </tr>
            </thead>
            <tbody>
              {execQ.isLoading && (
                <tr>
                  <td colSpan={6} className="px-3 py-8 text-center">
                    <Loader2 className="mx-auto animate-spin text-n-400" />
                  </td>
                </tr>
              )}
              {(execQ.data ?? []).map((e) => (
                <tr
                  key={e.id}
                  className={`cursor-pointer border-b border-n-100 last:border-b-0 hover:bg-n-50 ${
                    selected?.id === e.id ? 'bg-brand-50' : ''
                  }`}
                  onClick={() => setSelected(e)}
                >
                  <td className="px-[14px] py-[11px] text-n-700">#{e.id}</td>
                  <td className="px-[14px] py-[11px] text-n-700">contato {e.contact_id}</td>
                  <td className="px-[14px] py-[11px] text-n-600">
                    {new Date(e.started_at).toLocaleString('pt-BR')}
                  </td>
                  <td className="px-[14px] py-[11px]">
                    <span
                      className={`rounded-pill px-2 py-[2px] font-bold text-[10px] uppercase tracking-[.04em] ${STATUS_COLORS[e.status]}`}
                    >
                      {STATUS_LABELS[e.status]}
                    </span>
                  </td>
                  <td className="px-[14px] py-[11px] text-[11px] text-n-600">
                    {e.current_node_id ?? '—'}
                  </td>
                  <td className="px-[14px] py-[11px] text-right">
                    {(e.status === 'abandoned' || e.status === 'errored') && (
                      <button
                        type="button"
                        onClick={(ev) => {
                          ev.stopPropagation();
                          reprocessMut.mutate(e.id);
                        }}
                        className="inline-flex items-center gap-1 rounded border border-n-200 bg-n-0 px-2 py-1 text-[11px] font-semibold hover:bg-n-50"
                        title="Reprocessar"
                      >
                        <RotateCcw size={12} />
                        Reprocessar
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {!execQ.isLoading && (execQ.data ?? []).length === 0 && (
                <tr>
                  <td colSpan={6} className="px-3 py-12 text-center text-n-500">
                    Sem execuções nesse filtro.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {selected && (
          <aside className="w-[400px] overflow-auto rounded-md border border-n-200 bg-n-0 p-4 shadow-sm">
            <h3 className="font-bold text-[14px]">Execução #{selected.id}</h3>
            <div className="mt-3 space-y-3 text-[12px]">
              <Detail label="Status" value={STATUS_LABELS[selected.status]} />
              <Detail label="Nó atual" value={selected.current_node_id ?? '—'} mono />
              <Detail
                label="Iniciada"
                value={new Date(selected.started_at).toLocaleString('pt-BR')}
              />
              {selected.ended_at && (
                <Detail
                  label="Finalizada"
                  value={new Date(selected.ended_at).toLocaleString('pt-BR')}
                />
              )}
              <div>
                <span className="block font-semibold text-[11px] text-n-500 uppercase tracking-[.04em]">
                  Caminho de nós
                </span>
                <div className="mt-1 flex flex-wrap gap-1 font-mono text-[11px]">
                  {selected.node_path.length === 0 && <span className="text-n-500">—</span>}
                  {selected.node_path.map((n, i) => (
                    <span
                      key={`${n}-${i}`}
                      className="rounded bg-n-100 px-1.5 py-0.5 text-n-700"
                    >
                      {n}
                    </span>
                  ))}
                </div>
              </div>
              <div>
                <span className="block font-semibold text-[11px] text-n-500 uppercase tracking-[.04em]">
                  Estado
                </span>
                <pre className="mt-1 max-h-40 overflow-auto rounded bg-n-50 p-2 font-mono text-[11px]">
                  {JSON.stringify(selected.state, null, 2)}
                </pre>
              </div>
              {selected.error_message && (
                <div className="rounded border border-danger-200 bg-danger-50 p-2 text-[11px] text-danger-700">
                  <b>Erro:</b> {selected.error_message}
                </div>
              )}
            </div>
          </aside>
        )}
      </div>
    </div>
  );
}

function Detail({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <span className="block font-semibold text-[11px] text-n-500 uppercase tracking-[.04em]">
        {label}
      </span>
      <span className={`block ${mono ? 'font-mono' : ''} text-n-800`}>{value}</span>
    </div>
  );
}
